import React, { useRef, useEffect, useState } from "react";
import { Popper } from "@mui/material";
import "./TriggerInfoPopover.scss";
import {
  PulseTabs,
  PulseTabSizes,
  PulseTabTypes,
} from "@pulselabs-ai/shared-fe-ui";
import { MobileIcon } from "../icons/MobileIcon";
import { WebIcon } from "../icons/WebIcon";

// Define CustomTabPanel
interface TabPanelProps {
  children?: React.ReactNode;
  index: string; // Use string to match activeTab value
  value: string;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        // Optionally add some padding or Box wrapper if needed
        <>{children}</>
      )}
    </div>
  );
}

interface TriggerInfoPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  triggerType: string;
  title?: string;
  description?: string;
  imageSrc?: string;
  customImageSrc?: string;
  customOffset?: [number, number]; // Add optional custom offset prop
}

const descriptions = {
  Shake: "The feedback form appears when a user shakes their device",
  Screenshot:
    "The feedback form appears when a user uses their device to take a screen grab",
  FloatingButton:
    "The feedback form appears when a user taps the feedback button",
  Custom: "The feedback form appears when a user performs the custom action",
  ShakeWeb: "The feedback form appears when a user shakes their cursor*",
  ShakeWeb2: "*Requires connected mouse",
  ScreenshotWeb:
    "The feedback form appears when a user uses a predefined keyboard shortcut to take a screen grab",
  ScreenshotWeb2: "*Requires connected keyboard",
  FloatingButtonWeb:
    "The feedback form appears when a user clicks the feedback button",
  CustomWeb: "The feedback form appears when a user performs the custom action",
};

const TriggerInfoPopover: React.FC<TriggerInfoPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  triggerType,
  customOffset,
}) => {
  const popoverId = open ? "trigger-info-popper" : undefined;
  const popoverRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState("mobile");

  const defaultDescription =
    descriptions[triggerType as keyof typeof descriptions] ||
    "Information about this trigger type.";

  // Define web description by appending 'Web' to the triggerType
  const defaultWebDescription =
    descriptions[`${triggerType}Web` as keyof typeof descriptions] ||
    "Information about this web trigger type.";

  const defaultWebDescription2 =
    descriptions[`${triggerType}Web2` as keyof typeof descriptions] || "";

  // Handle clicks outside to close the popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, onClose, anchorEl]);

  const getImageSrc = (type: string) => {
    switch (type) {
      case "Shake":
        return "/images/shake_mobile.png";
      case "Screenshot":
        return "/images/screenshot_mobile.gif";
      case "FloatingButton":
        return "/images/feedback_mobile.png";
      case "Custom":
        return "/images/custom_mobile.png";
      case "ShakeWeb":
        return "/images/shake_web.png";
      case "ScreenshotWeb":
        return "/images/screenshot_web.png";
      case "FloatingButtonWeb":
        return "/images/feedback_web.png";
      case "CustomWeb":
        return "/images/custom_web.png";
      default:
        return undefined;
    }
  };

  const imagePath = getImageSrc(triggerType);
  const imagePathWeb = getImageSrc(triggerType + "Web");

  // Define the default offset
  const defaultOffset: [number, number] = [0, 0];

  return (
    <Popper
      id={popoverId}
      open={open}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: {
            // Use customOffset if provided, otherwise defaultOffset
            offset: customOffset ?? defaultOffset,
          },
        },
      ]}
      sx={{ zIndex: 1400 }}
    >
      <div className="trigger-info-panel" ref={popoverRef}>
        <PulseTabs
          size={PulseTabSizes.EXTRA_SMALL}
          type={PulseTabTypes.SEGMENTED}
          value={activeTab}
          onTabChange={(newValue: string) => setActiveTab(newValue)}
          tabOptions={[
            { label: "Mobile", value: "mobile", startIcon: <MobileIcon /> },
            { label: "Web", value: "web", startIcon: <WebIcon /> },
          ]}
        />
        <CustomTabPanel value={activeTab} index="mobile">
          <div className="body-s --color-text-secondary pt-2">
            {defaultDescription}
          </div>

          {imagePath && (
            <div className="trigger-image">
              <img
                src={imagePath}
                alt={`${triggerType} trigger`}
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          )}
        </CustomTabPanel>
        <CustomTabPanel value={activeTab} index="web">
          <div className="body-s --color-text-secondary pt-2">
            {defaultWebDescription}
          </div>
          {defaultWebDescription2 && (
            <div className="body-s --color-text-secondary pt-2">
              {defaultWebDescription2}
            </div>
          )}

          {imagePath && (
            <div className="trigger-image">
              <img src={imagePathWeb} alt={`${triggerType} trigger`} />
            </div>
          )}
        </CustomTabPanel>
      </div>
    </Popper>
  );
};

export default TriggerInfoPopover;
